import fp from 'lodash/fp';
import React, { useMemo, useEffect, useRef, useState } from 'react';


export const useMemoDeps = <TResult>(
    factory: (...args: any[]) => TResult,
    deps: React.DependencyList,
    // eslint-disable-next-line react-hooks/exhaustive-deps
): TResult => useMemo(() => factory(...deps), [factory, ...deps]);

export const useUpdateOnlyEffect = (
    effect: React.EffectCallback,
    deps: React.DependencyList,
): void => {
    const isFirstRun = useRef(true);
    useEffect(() => {
        if (isFirstRun.current) {
            isFirstRun.current = false;
            return undefined;
        }
        return effect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, deps);
};

export const usePrevious = <TValue>(value: TValue): TValue | undefined => {
    const ref = useRef<TValue>();
    useEffect(() => {
        ref.current = value;
    });
    return ref.current;
};

export const useTimeoutEffect = (callback: () => void, timeout: number) =>
    useEffect(() => {
        const timer = setTimeout(callback, timeout);
        return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

export const useStateRef = <TValue>(initialValue: TValue): [
    TValue,
    React.Dispatch<React.SetStateAction<TValue>>,
    React.RefObject<TValue>
] => {
    const [value, setValue] = useState(initialValue);
    const stateRef = useRef<TValue>(value);
    const setValueAndRef = useMemo<React.Dispatch<React.SetStateAction<TValue>>>(
        () => value => {
            if (fp.isFunction(value)) {
                setValue(prevState => {
                    const newValue = value(prevState);
                    stateRef.current = newValue;
                    return newValue;
                });
            }
            stateRef.current = value as TValue;
            setValue(value);
        },
        [],
    );
    return [value, setValueAndRef, stateRef];
};
